import { Component, OnDestroy, OnInit, Signal, TemplateRef, ViewChild, inject } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { ActivatedRoute, ParamMap } from "@angular/router";
import { AggregateManagerService } from "@tylertech/aggregates-module";
import { ChartManagerService } from "@tylertech/charting-module";
import { CommonDialogsService } from "@tylertech/common-dialogs-module";
import { FeatureFlagHelperService, NavMenuService, SecurityService, UserProfileService } from "@tylertech/core-module";
import { ShellService } from "@tylertech/incode-module";
import { IAggregate, IChartCriteria } from "@tylertech/tyler-api-types";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

const fifteenMinutes = 15 * 60 * 1000; // 900000;

@Component({
    selector: "dashboard",
    templateUrl: "./dashboard.tmpl.html",
    styleUrls: ["./dashboard.component.scss"],
    standalone: false
})
export class DashboardComponent implements OnDestroy, OnInit
{
    private readonly $securityService = inject(SecurityService);
    private readonly $userProfileService = inject(UserProfileService);
    private readonly $navMenuService = inject(NavMenuService);
    private readonly $commonDialogsService = inject(CommonDialogsService);
    private readonly $shellService = inject(ShellService);
    private readonly $chartManagerService = inject(ChartManagerService);
    private readonly $aggregateManagerService = inject(AggregateManagerService);
    private readonly matDialog = inject(MatDialog);

    private readonly destructor$ = new Subject<void>();
    readonly title = "Dashboard";
    readonly page: number = 1;

    classAnimation: string = "rubberBand";
    purchasingLicense: boolean = true;
    isProcessing: boolean;
    fetched: boolean;
    timeElapsed: boolean;
    chartsLoaded: boolean;
    leftCharts: Array<IChartCriteria>;
    rightCharts: Array<IChartCriteria>;
    hiddenCharts: Array<IChartCriteria>;
    chartAggregates: IAggregate[];

    private refreshTimeoutId: ReturnType<typeof setTimeout>;

    @ViewChild("ieWarning")
    private readonly ieWarningTemplate: TemplateRef<HTMLElement>;

    readonly devFlag: Signal<boolean>;

    constructor()
    {
        const $chartManagerService = this.$chartManagerService;
        const featureFlagService = inject(FeatureFlagHelperService);
        const activatedRoute = inject(ActivatedRoute);

        activatedRoute.paramMap.subscribe(
            (params: ParamMap) =>
            {
                if (params.has("module"))
                {
                    // TODO(DO-4549) - We never read this data here. So who are we "communicating" with? And why are we doing it through session storage?
                    window.sessionStorage.removeItem("current_module");
                    window.sessionStorage.removeItem("module_display");

                    this.$navMenuService.setDisabled(true);
                }
            },
            error => console.error(error)
        );

        $chartManagerService.leftChartsObservable
            .pipe(takeUntil(this.destructor$))
            .subscribe((charts: Array<IChartCriteria>) =>
            {
                this.leftCharts = charts;
            });

        $chartManagerService.rightChartsObservable
            .pipe(takeUntil(this.destructor$))
            .subscribe((charts: Array<IChartCriteria>) =>
            {
                this.rightCharts = charts;
            });

        $chartManagerService.hiddenChartsObservable
            .pipe(takeUntil(this.destructor$))
            .subscribe((charts: Array<IChartCriteria>) =>
            {
                this.hiddenCharts = charts;
            });

        $chartManagerService.chartsLoadedObservable
            .pipe(takeUntil(this.destructor$))
            .subscribe(() =>
            {
                this.chartsLoaded = true;
            });

        this.devFlag = featureFlagService.DevFlag;
    }

    // TODO - why is this hard-coded here?
    private get isHrAnalyst(): boolean { return this.$securityService.hasPersona("HR Analyst"); }
    // TODO - why is this hard-coded here?
    private get isHrDirector(): boolean { return this.$securityService.hasPersona("HR Director"); }
    // TODO - why is this hard-coded here?
    private get isPyAnalyst(): boolean { return this.$securityService.hasPersona("Payroll Analyst"); }
    // TODO - why is this hard-coded here?
    private get isPySupervisor(): boolean { return this.$securityService.hasPersona("Payroll Supervisor"); }

    get embedded(): boolean { return this.$shellService.isEmbedded(); }
    get aggregatesLoaded(): boolean { return this.$aggregateManagerService.AggregatesLoaded; }
    get aggregateChartsLoaded(): boolean { return this.$aggregateManagerService.ChartAggregatesLoaded; }

    // TODO - why is this hard-coded here?
    get showEmployeeLookup(): boolean
    {
        return this.isHrDirector
            || this.isHrAnalyst
            || this.isPyAnalyst
            || this.isPySupervisor;
    }

    ngOnDestroy(): void
    {
        if (this.refreshTimeoutId !== undefined)
            clearTimeout(this.refreshTimeoutId);
        this.destructor$.next();
        this.destructor$.complete();
    }

    ngOnInit(): void
    {
        this.refresh()
            .finally(() =>
            {
                this.fetched = true;
            });

        this.checkBrowser();
        window.opener = null;
    }

    private checkBrowser(): void
    {
        const userAgent = window.navigator.userAgent;
        if (userAgent.indexOf("MSIE ") > 0 || userAgent.indexOf("Trident/") > 0)
        {
            this.launchIEWarning();
        }
    }

    private launchIEWarning(): void
    {
        this.matDialog.open(this.ieWarningTemplate);
    }

    refresh(): Promise<void>
    {
        this.timeElapsed = false;
        if (this.refreshTimeoutId !== undefined)
            clearTimeout(this.refreshTimeoutId);

        return Promise.all(
            [
                this.$aggregateManagerService.RefetchAggregates(),
                this.$chartManagerService.updateCharts()
            ])
            .then(() =>
            {
                this.refreshTimeoutId = setTimeout(() => this.timeElapsed = true, fifteenMinutes);
            });
    }

    getChartAggregates(column: number): IAggregate[]
    {
        return this.$aggregateManagerService.GetChartAggregatesForColumn(column);
    }

    displayChartOrdering(): void
    {
        const originalLeftCharts = [...this.leftCharts];
        const originalRightCharts = [...this.rightCharts];
        const originalHiddenCharts = [...this.hiddenCharts];

        const groups = [
            this.leftCharts,
            this.rightCharts,
            this.hiddenCharts
        ];
        this.$commonDialogsService.showDragAndDropDialog(groups, "Title", "Application", "Dashboard Configuration", "Smart Charts", ["Left", "Right", "Hidden"])
            .then(
                r =>
                {
                    if (r)
                        this.saveChartSettings(r);
                    else
                    {
                        this.leftCharts = originalLeftCharts;
                        this.rightCharts = originalRightCharts;
                        this.hiddenCharts = originalHiddenCharts;
                    }
                },
                () =>
                {
                    window.location.reload();
                }
            );
    }

    private saveChartSettings(groups: IChartCriteria[][]): void
    {
        this.$userProfileService.SetPositions(groups)
            .finally(() =>
            {
                this.isProcessing = false;
            });
    }

    trackByChartId(_index: number, chart: IChartCriteria): string
    {
        return chart.ChartId;
    }

    trackByAggregateId(_index: number, agg: IAggregate): string
    {
        return `${agg.AggregateProviderId}:${agg.Id}`;
    }
}
